import { useEffect, useMemo, useState } from "react";
import type {
  DefaultMaterialsByActKindSettings,
  InsuranceNetwork,
  TherapeuticMaterialCode,
  TherapeuticMaterialFamily,
} from "cadendar-shared";
import {
  createEmptyDefaultMaterialsByActKind,
  getAllowedTherapeuticMaterialOptions,
  getTherapeuticMaterialOptionByCode,
  insuranceNetworkLabels,
  insuranceNetworkValues,
  normalizeDefaultMaterialsByActKind,
  therapeuticMaterialFamilyLabels,
  therapeuticMaterialFamilyValues,
} from "cadendar-shared";
import Button from "../../main/components/Button";
import { trpc } from "../../main/components/trpc.ts";
import {
  getDefaultMaterialsErrorMessage,
  useDefaultMaterialsByActKind,
} from "../hooks/useDefaultMaterialsByActKind";

interface MaterialSelectProps {
  label: string;
  value: TherapeuticMaterialCode | null | undefined;
  options: ReadonlyArray<{
    code: TherapeuticMaterialCode;
    selectLabel: string;
  }>;
  allowDefault?: boolean;
  disabled?: boolean;
  onChange: (next: TherapeuticMaterialCode | null | undefined) => void;
}

const MaterialSelect = ({
  label,
  value,
  options,
  allowDefault,
  disabled,
  onChange,
}: MaterialSelectProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (next === "__default") {
      onChange(undefined);
      return;
    }

    if (next === "") {
      onChange(null);
      return;
    }

    onChange(options.find((option) => option.code === next)?.code ?? null);
  };

  const current = value ? getTherapeuticMaterialOptionByCode(value) : null;
  const displayLabel = current?.selectLabel ?? value ?? "";

  return (
    <label className="flex flex-col gap-1 text-sm text-slate-700">
      <span className="font-medium text-slate-800">{label}</span>
      <select
        className="rounded-lg border border-slate-300 bg-white px-3 py-2 shadow-sm focus:border-slate-400 focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
        value={value === undefined ? "__default" : (value ?? "")}
        onChange={handleChange}
        disabled={disabled}
      >
        {allowDefault ? (
          <option value="__default">Utiliser le défaut</option>
        ) : null}
        <option value="">Aucun</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.selectLabel}
          </option>
        ))}
      </select>
      {value && !current ? (
        <span className="text-xs text-red-700">
          Matériau non reconnu pour ce praticien/réseau : {displayLabel}
        </span>
      ) : null}
    </label>
  );
};

const PEC_NETWORKS: ReadonlyArray<InsuranceNetwork> = [
  "santeclair",
  "itelis",
  "seveane",
];

const DefaultMaterialsByActKindPage = () => {
  const defaultMaterials = useDefaultMaterialsByActKind();
  const prefsQuery = trpc.preference.get.useQuery();

  const activePecNetworks = useMemo(
    () =>
      PEC_NETWORKS.filter(
        (network) => prefsQuery.data?.insurance_networks?.[network] === true,
      ),
    [prefsQuery.data],
  );

  const [draft, setDraft] = useState<DefaultMaterialsByActKindSettings | null>(
    null,
  );
  const [draftOwnerId, setDraftOwnerId] = useState<string | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  //: REVIEW: useEffect nécessaire pour synchroniser le draft avec la réponse serveur.
  useEffect(() => {
    if (defaultMaterials.settings) {
      setDraft(normalizeDefaultMaterialsByActKind(defaultMaterials.settings));
      setDraftOwnerId(defaultMaterials.ownerId);
      setSaveFeedback(null);
    }
  }, [defaultMaterials.ownerId, defaultMaterials.settings]);

  const resolvedDraft =
    (draftOwnerId === defaultMaterials.ownerId ? draft : null) ??
    defaultMaterials.settings ??
    createEmptyDefaultMaterialsByActKind();
  const baseline = normalizeDefaultMaterialsByActKind(
    defaultMaterials.settings,
  );
  const isDirty = JSON.stringify(resolvedDraft) !== JSON.stringify(baseline);

  const updateFamilyDefault = (
    family: TherapeuticMaterialFamily,
    next: TherapeuticMaterialCode | null,
  ) => {
    setDraftOwnerId(defaultMaterials.ownerId);
    setDraft((current) => {
      const base = normalizeDefaultMaterialsByActKind(current ?? resolvedDraft);
      return {
        ...base,
        defaultByFamily: {
          ...base.defaultByFamily,
          [family]: next,
        },
      };
    });
  };

  const updateNetworkOverride = (
    network: InsuranceNetwork,
    family: TherapeuticMaterialFamily,
    next: TherapeuticMaterialCode | null | undefined,
  ) => {
    setDraftOwnerId(defaultMaterials.ownerId);
    setDraft((current) => {
      const base = normalizeDefaultMaterialsByActKind(current ?? resolvedDraft);
      const currentOverrides = base.networkOverrides[network] ?? {};

      if (next === undefined) {
        const { [family]: removedOverride, ...remainingOverrides } =
          currentOverrides;

        return {
          ...base,
          networkOverrides: {
            ...base.networkOverrides,
            [network]: remainingOverrides,
          },
        };
      }

      return {
        ...base,
        networkOverrides: {
          ...base.networkOverrides,
          [network]: {
            ...currentOverrides,
            [family]: next,
          },
        },
      };
    });
  };

  const handleSaveSettings = async () => {
    try {
      const saved = await defaultMaterials.saveSettings(resolvedDraft);
      setDraft(saved);
      setDraftOwnerId(defaultMaterials.ownerId);
      setSaveFeedback("Matériaux enregistrés.");
    } catch {
      // The mutation error is displayed below the form.
    }
  };

  if (defaultMaterials.isLoading) {
    return <div className="p-6 text-slate-600">Chargement des réglages...</div>;
  }

  if (defaultMaterials.error) {
    return (
      <div className="p-6 text-red-700">
        {getDefaultMaterialsErrorMessage(defaultMaterials.error)}
      </div>
    );
  }

  return (
    <div className="bg-slate-100 p-4">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-950">
                Matériaux par défaut par type d&apos;acte
              </h1>
              <p className="mt-2 max-w-4xl text-sm text-slate-600">
                Configurez les matériaux utilisés par défaut lors de la saisie
                d&apos;un plan de traitement. Les surcharges réseau prennent le
                dessus sur le défaut global, et restent filtrées par les
                matériaux autorisés pour chaque réseau.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                title="Réinitialiser"
                onClick={() => {
                  setDraft(baseline);
                  setDraftOwnerId(defaultMaterials.ownerId);
                  setSaveFeedback(null);
                }}
                disabled={!isDirty || defaultMaterials.isSaving}
              />
              <Button
                title={
                  defaultMaterials.isSaving
                    ? "Enregistrement..."
                    : "Enregistrer"
                }
                onClick={handleSaveSettings}
                style="main"
                disabled={!isDirty || defaultMaterials.isSaving}
              />
            </div>
          </div>

          {saveFeedback ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {saveFeedback}
            </div>
          ) : null}

          {defaultMaterials.saveError ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {getDefaultMaterialsErrorMessage(defaultMaterials.saveError)}
            </div>
          ) : null}
        </section>

        <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Défaut</h2>
          <p className="mb-4 text-sm text-slate-600">
            Matériau utilisé par défaut pour chaque type d&apos;acte,
            indépendamment du réseau.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {therapeuticMaterialFamilyValues.map((family) => {
              const current = resolvedDraft.defaultByFamily[family] ?? null;
              const options = getAllowedTherapeuticMaterialOptions({
                family,
                network: null,
              });
              return (
                <MaterialSelect
                  key={family}
                  label={therapeuticMaterialFamilyLabels[family]}
                  value={current}
                  options={options}
                  disabled={defaultMaterials.isSaving}
                  onChange={(next) => {
                    if (next !== undefined) {
                      updateFamilyDefault(family, next);
                    }
                  }}
                />
              );
            })}
          </div>
        </section>

        {activePecNetworks.length === 0 ? (
          <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            Aucun réseau PEC actif dans les préférences du praticien.
          </section>
        ) : (
          activePecNetworks.map((network) => (
            <section
              key={network}
              className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200"
            >
              <h2 className="text-lg font-semibold text-slate-900">
                Surcharge {insuranceNetworkLabels[network]}
              </h2>
              <p className="mb-4 text-sm text-slate-600">
                Matériaux spécifiques au réseau{" "}
                {insuranceNetworkLabels[network]}. Les options non autorisées
                pour ce réseau ne sont pas proposées.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {therapeuticMaterialFamilyValues.map((family) => {
                  const current =
                    resolvedDraft.networkOverrides[network]?.[family];
                  const options = getAllowedTherapeuticMaterialOptions({
                    family,
                    network,
                  });
                  return (
                    <MaterialSelect
                      key={family}
                      label={therapeuticMaterialFamilyLabels[family]}
                      value={current}
                      options={options}
                      allowDefault
                      disabled={defaultMaterials.isSaving}
                      onChange={(next) =>
                        updateNetworkOverride(network, family, next)
                      }
                    />
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default DefaultMaterialsByActKindPage;
